
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export const FadeIn: React.FC<{ 
  children: React.ReactNode; 
  delay?: number;
  duration?: number;
  className?: string;
}> = ({ 
  children, 
  delay = 0,
  duration = 0.5,
  className = "" 
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: duration,
        delay: delay / 10,
        ease: "easeOut"
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const WaveBackground: React.FC<{
  height?: string;
  className?: string;
}> = ({ 
  height,
  className = "" 
}) => {
  return (
    <div 
      className={`w-full overflow-hidden ${className}`}
      style={height ? { height } : undefined}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full">
        <path 
          fill="#E27D60" 
          fillOpacity="1" 
          d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z">
        </path>
      </svg>
    </div>
  );
};

export const CircleDecoration: React.FC<{
  size: number;
  color: string;
  className?: string;
}> = ({ size, color, className = "" }) => {
  return (
    <div 
      className={`rounded-full ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: color,
      }}
    />
  );
};

export const DotsPattern: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  return (
    <div className={`absolute ${className}`}>
      <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="10" cy="40" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="10" cy="70" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="10" cy="100" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="40" cy="10" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="40" cy="40" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="40" cy="70" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="40" cy="100" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="70" cy="10" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="70" cy="40" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="70" cy="70" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="70" cy="100" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="100" cy="10" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="100" cy="40" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="100" cy="70" r="4" fill="#E27D60" opacity="0.3" />
        <circle cx="100" cy="100" r="4" fill="#E27D60" opacity="0.3" />
      </svg>
    </div>
  );
};

export const WaveDivider: React.FC<{
  position?: "top" | "bottom";
  color?: string;
  className?: string;
}> = ({ 
  position = "bottom",
  color = "#E27D60",
  className = "" 
}) => {
  return (
    <div className={`w-full overflow-hidden ${className}`}>
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 1440 320" 
        className={`w-full ${position === "top" ? "rotate-180" : ""}`}
      >
        <path 
          fill={color}
          fillOpacity="1" 
          d="M0,96L48,112C96,128,192,160,288,170.7C384,181,480,171,576,144C672,117,768,75,864,69.3C960,64,1056,96,1152,106.7C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z">
        </path>
      </svg>
    </div>
  );
};

export const Confetti: React.FC<{
  colors: string[];
  pieces?: number;
  duration?: number;
}> = ({ colors, pieces = 50, duration = 3000 }) => {
  const [confettiPieces, setConfettiPieces] = useState<React.ReactNode[]>([]);
  
  useEffect(() => {
    const newPieces = [];
    
    for (let i = 0; i < pieces; i++) {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = Math.random() * 10 + 5; // 5-15px
      const xStart = Math.random() * 100; // 0-100%
      const yStart = Math.random() * 20 - 10; // -10-10% (to start above and at the top)
      const rotate = Math.random() * 360; // 0-360deg
      const delay = Math.random() * 0.5; // 0-0.5s
      
      newPieces.push(
        <motion.div
          key={i}
          initial={{ 
            x: `${xStart}vw`, 
            y: `${yStart}vh`, 
            rotate: 0,
            opacity: 1 
          }}
          animate={{ 
            y: '100vh', 
            rotate: rotate,
            opacity: 0 
          }}
          transition={{ 
            duration: (Math.random() * 1 + 1.5), 
            ease: 'easeOut',
            delay: delay
          }}
          style={{
            position: 'absolute',
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: color,
            borderRadius: '50%',
            zIndex: 50
          }}
        />
      );
    }
    
    setConfettiPieces(newPieces);
    
    const timer = setTimeout(() => {
      setConfettiPieces([]);
    }, duration);
    
    return () => clearTimeout(timer);
  }, [colors, pieces, duration]);
  
  return <div className="fixed inset-0 pointer-events-none">{confettiPieces}</div>;
};

